/**
 * Splits main collection product grid into Express vs Next Day Delivery from tags + user city.
 *
 * IMPORTANT: Disconnect the staging MutationObserver while moving nodes. Otherwise each
 * appendChild/removal fires the observer → redistribute → more mutations → infinite loop
 * and the collection tab freezes.
 */
import { getDeliveryType, parseTagsString } from './skipp-product-delivery-type.js';
import { isLocationEnforcementSatisfied } from './skipp-location-preference.js';

export { getDeliveryType } from './skipp-product-delivery-type.js';

const SPLIT_ROOT = '[data-skipp-collection-split]';
const TOGGLE_ROOT = '[data-skipp-delivery-toggle-root]';
const DELIVERY_TAB_STORAGE_KEY = 'skipp:collection-delivery-tab';

function getToggleRoot() {
  return document.querySelector(TOGGLE_ROOT);
}

function loadRememberedDeliveryTab() {
  try {
    return String(sessionStorage.getItem(DELIVERY_TAB_STORAGE_KEY) || '').trim();
  } catch (_e) {
    return '';
  }
}

function rememberDeliveryTab(tab) {
  try {
    if (tab === 'express' || tab === 'next') {
      sessionStorage.setItem(DELIVERY_TAB_STORAGE_KEY, tab);
    }
  } catch (_e) {
    /* ignore */
  }
}

function isCollectionSplitMweb() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(max-width: 749px)').matches
  );
}

/**
 * Toggle UI: desktop only — Express vs Next single column. Mobile stacks both lists (toggle hidden via CSS).
 */
export function syncDeliveryToggleView(root) {
  const toggleRoot = getToggleRoot();
  if (!toggleRoot || !root || root.dataset.skippHasDeliveryToggle !== 'true') return;

  const expressSection = root.querySelector('[data-skipp-express-section]');
  const nextSection = root.querySelector('[data-skipp-next-section]');
  const expressUl = document.getElementById('product-grid-express');
  const nextUl = document.getElementById('product-grid-next');
  if (!expressSection || !nextSection || !expressUl || !nextUl) return;

  if (isCollectionSplitMweb()) {
    expressSection.hidden = expressUl.children.length === 0;
    nextSection.hidden = nextUl.children.length === 0;
    return;
  }

  let mode =
    String(toggleRoot.dataset.activeTab || '').trim() ||
    loadRememberedDeliveryTab();

  const expressCount = expressUl.children.length;
  if (mode !== 'express' && mode !== 'next') {
    mode = expressCount > 0 ? 'express' : 'next';
  }
  toggleRoot.dataset.activeTab = mode;

  const toggleBar = toggleRoot.querySelector('.delivery-toggle');
  if (toggleBar) {
    toggleBar.classList.toggle('delivery-toggle--express', mode === 'express');
    toggleBar.classList.toggle('delivery-toggle--next', mode === 'next');
  }

  toggleRoot.querySelectorAll('.delivery-toggle__btn').forEach((btn) => {
    const t = btn.getAttribute('data-type');
    const on = t === mode;
    btn.setAttribute('aria-selected', on ? 'true' : 'false');
  });

  const emptyExpress = toggleRoot.querySelector('[data-skipp-toggle-express-empty]');
  root.querySelectorAll('.skipp-collection-split__count-mirror-wrap').forEach((mirror) => {
    mirror.hidden = false;
  });

  if (mode === 'express') {
    expressSection.hidden = false;
    nextSection.hidden = true;
    if (emptyExpress) emptyExpress.hidden = expressUl.children.length > 0;
  } else {
    expressSection.hidden = true;
    nextSection.hidden = false;
    if (emptyExpress) emptyExpress.hidden = true;
  }
}

function attachDeliveryToggleControls(root) {
  const toggleRoot = getToggleRoot();
  if (!toggleRoot || !root || root.dataset.skippHasDeliveryToggle !== 'true') return;

  if (toggleRoot._skippDeliveryToggleDelegated) return;
  toggleRoot._skippDeliveryToggleDelegated = true;

  toggleRoot.addEventListener('click', (ev) => {
    const btn = ev.target.closest('.delivery-toggle__btn');
    if (!btn || !toggleRoot.contains(btn)) return;
    const t = btn.getAttribute('data-type');
    if (t !== 'express' && t !== 'next') return;
    toggleRoot.dataset.activeTab = t;
    rememberDeliveryTab(t);
    syncDeliveryToggleView(root);
  });

  toggleRoot.querySelector('.delivery-toggle__track')?.addEventListener('keydown', (ev) => {
    if (ev.key !== 'ArrowLeft' && ev.key !== 'ArrowRight') return;
    const tabs = [...toggleRoot.querySelectorAll('.delivery-toggle__btn[data-type]')];
    const i = tabs.indexOf(document.activeElement);
    if (i < 0) return;
    const next = tabs[ev.key === 'ArrowRight' ? Math.min(tabs.length - 1, i + 1) : Math.max(0, i - 1)];
    next?.focus();
  });
}

function setBadge(li, isExpress) {
  const label = isExpress ? '30 min' : 'Next day';
  li.querySelectorAll('[data-skipp-delivery-badge]').forEach((el) => {
    el.hidden = false;
    el.textContent = label;
    el.classList.toggle('skipp-delivery-badge--express', isExpress);
    el.classList.toggle('skipp-delivery-badge--next', !isExpress);
  });
}

let stagingObserver = null;

export function redistributeSkippCollectionDelivery() {
  const root = document.querySelector(SPLIT_ROOT);
  const staging = document.getElementById('product-grid');
  const expressUl = document.getElementById('product-grid-express');
  const nextUl = document.getElementById('product-grid-next');
  if (!root || !staging || !expressUl || !nextUl) return;

  stagingObserver?.disconnect();

  try {
    /*
     * Infinite scroll appends new <li>s into staging while older cards live in express/next.
     * If we appended column items after those orphans, staging order became [new…][old…] so
     * re-split stacked new pages above older ones in each column. Hold pending staging rows first,
     * rebuild [express column][next column][pending newest batch] — then distribute.
     */
    const pendingFrag = document.createDocumentFragment();
    for (const li of [...staging.querySelectorAll(':scope > li.grid__item')]) {
      pendingFrag.appendChild(li);
    }
    for (const li of [...expressUl.querySelectorAll(':scope > li')]) {
      staging.appendChild(li);
    }
    for (const li of [...nextUl.querySelectorAll(':scope > li')]) {
      staging.appendChild(li);
    }
    staging.appendChild(pendingFrag);

    if (!isLocationEnforcementSatisfied()) {
      for (const li of staging.querySelectorAll(':scope > li.grid__item')) {
        li.querySelectorAll('[data-skipp-delivery-badge]').forEach((el) => {
          el.hidden = true;
        });
      }
      const expressSection = root.querySelector('[data-skipp-express-section]');
      const nextSection = root.querySelector('[data-skipp-next-section]');
      if (expressSection) expressSection.hidden = true;
      if (nextSection) nextSection.hidden = true;
      const toggleRootEl = getToggleRoot();
      if (toggleRootEl) toggleRootEl.hidden = true;
      staging.classList.remove('skipp-collection-grid--staging--drained');
      root.querySelectorAll('.skipp-collection-split__count-mirror-wrap').forEach((el) => {
        el.hidden = true;
      });
      return;
    }

    const city = window.SkippUserLocation?.getUserLocation?.()?.city || '';
    const items = [...staging.querySelectorAll(':scope > li.grid__item')];
    const debug = typeof window !== 'undefined' && window.__SKIPP_DEBUG_PRODUCT_TAGS__;

    for (const li of items) {
      const raw = li.getAttribute('data-skipp-tags') || '';
      const title = li.getAttribute('data-skipp-product-title') || '';
      const tags = parseTagsString(raw);
      if (debug) {
        console.log(title, tags);
      }
      const isEx = getDeliveryType({ tags: raw }, city) === 'express';
      const target = isEx ? expressUl : nextUl;
      target.appendChild(li);
      setBadge(li, isEx);
    }

    const expressSection = root.querySelector('[data-skipp-express-section]');
    const nextSection = root.querySelector('[data-skipp-next-section]');
    const hasDeliveryToggleMarkup = root.dataset.skippHasDeliveryToggle === 'true';
    const toggleRootEl = getToggleRoot();
    const expressCount = expressUl.children.length;
    const nextCount = nextUl.children.length;
    const showToggleUI = hasDeliveryToggleMarkup && expressCount > 0 && nextCount > 0;
    const isMweb = isCollectionSplitMweb();

    if (isMweb && hasDeliveryToggleMarkup) {
      if (toggleRootEl) toggleRootEl.hidden = true;
      if (expressSection) expressSection.hidden = expressCount === 0;
      if (nextSection) nextSection.hidden = nextCount === 0;
      root.querySelectorAll('.skipp-collection-split__count-mirror-wrap').forEach((el) => {
        el.hidden = true;
      });
    } else {
      if (toggleRootEl) {
        toggleRootEl.hidden = !showToggleUI;
      }

      if (!hasDeliveryToggleMarkup) {
        if (expressSection) {
          expressSection.hidden = expressUl.children.length === 0;
        }
        if (nextSection) {
          nextSection.hidden = nextUl.children.length === 0;
        }
        root.querySelectorAll('.skipp-collection-split__count-mirror-wrap').forEach((el) => {
          el.hidden = expressUl.children.length > 0;
        });
      } else if (!showToggleUI) {
        if (expressSection) {
          expressSection.hidden = expressCount === 0;
        }
        if (nextSection) {
          nextSection.hidden = nextCount === 0;
        }
        root.querySelectorAll('.skipp-collection-split__count-mirror-wrap').forEach((el) => {
          el.hidden = expressCount > 0;
        });
      }

      if (hasDeliveryToggleMarkup && showToggleUI) {
        attachDeliveryToggleControls(root);
        syncDeliveryToggleView(root);
      }
    }

    staging.classList.toggle('skipp-collection-grid--staging--drained', staging.children.length === 0);
  } finally {
    observeStaging();
  }
}

function observeStaging() {
  const staging = document.getElementById('product-grid');
  if (!staging || !staging.classList.contains('skipp-collection-grid--staging')) return;

  stagingObserver?.disconnect();
  stagingObserver = new MutationObserver(() => {
    redistributeSkippCollectionDelivery();
  });
  stagingObserver.observe(staging, { childList: true });
}

function patchFacetRerender() {
  const FF = window.FacetFiltersForm;
  if (!FF || typeof FF.renderProductGridContainer !== 'function' || FF._skippDeliverySplitPatched) return;
  FF._skippDeliverySplitPatched = true;
  const orig = FF.renderProductGridContainer.bind(FF);
  FF.renderProductGridContainer = function (html) {
    const preservedScroll =
      typeof window.scrollY === 'number' ? window.scrollY : window.pageYOffset || 0;
    orig(html);
    queueMicrotask(() => {
      redistributeSkippCollectionDelivery();
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          const maxY = Math.max(
            0,
            document.documentElement.scrollHeight - window.innerHeight
          );
          window.scrollTo(0, Math.min(preservedScroll, maxY));
        });
      });
    });
  };
}

function init() {
  const root = document.querySelector(SPLIT_ROOT);
  if (!root) return;
  patchFacetRerender();
  redistributeSkippCollectionDelivery();

  const mq = typeof window.matchMedia === 'function' ? window.matchMedia('(max-width: 749px)') : null;
  if (mq && typeof mq.addEventListener === 'function') {
    mq.addEventListener('change', () => redistributeSkippCollectionDelivery());
  } else if (mq && typeof mq.addListener === 'function') {
    mq.addListener(() => redistributeSkippCollectionDelivery());
  }

  document.addEventListener('skipp:user-location-changed', () => {
    redistributeSkippCollectionDelivery();
  });
  document.addEventListener('skipp:location-api-ready', () => {
    redistributeSkippCollectionDelivery();
  });
  document.addEventListener('skipp:location-preference-changed', () => {
    redistributeSkippCollectionDelivery();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
